import React from 'react';
import axios from 'axios';

import {
  Box,
  Button,
  CircularProgress,
  Divider,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { BiErrorCircle, BiMinusCircle, BiPencil, BiSolidCheckCircle, BiTrash } from 'react-icons/bi';
import { toast } from 'react-toastify';

import { useAddressSuggestions } from '@hooks/useAddressSuggestions';
import type { AddressSuggestion } from '@hooks/useAddressSuggestions';
import { validatePhone } from '@services/validationService';
import { useUser } from '@store';
import type { StopFieldValues } from '../hooks/useRunEditMode';

// ── Types ------------------------------------------------------------------ //

interface RunStopEditFormProps {
  /** Draft position of the stop (drives the "EDITING STOP N" footer label). */
  stopOrder: number;
  /** Staged values (baseline merged with edits) — the form is controlled. */
  values: StopFieldValues;
  customerId: number | null;
  /** Whether this stop has staged edits (enables Revert). */
  hasChanges: boolean;
  onChange: (patch: Partial<StopFieldValues>) => void;
  onRevert: () => void;
  onRemove: () => void;
  onDone: () => void;
}

// ── Helpers ----------------------------------------------------------------- //

/** Formats a phone number progressively as the user types (AddAppointmentDialog's mask). */
const formatPhoneProgressive = (value: string): string => {
  const raw = value.replace(/\D/g, '');
  const digits = raw.length > 10 && raw.startsWith('1') ? raw.slice(1) : raw;
  const len = digits.length;
  if (len === 0) return '';
  if (len <= 3) return `(${digits}`;
  if (len <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
};

/** Digits-only form of a phone — what stages and persists (matches what appointments store). */
const phoneDigits = (value: string): string => {
  const raw = value.replace(/\D/g, '');
  return (raw.length > 10 && raw.startsWith('1') ? raw.slice(1) : raw).slice(0, 10);
};

/** External label above the input, per the edit-mode comp. */
const Field: React.FC<{ label: string; children: React.ReactNode; sx?: object }> = ({ label, children, sx }) => (
  <Box sx={sx}>
    <Typography variant='caption' sx={{ fontWeight: 600, color: 'text.secondary', display: 'block', mb: 0.5 }}>
      {label}
    </Typography>
    {children}
  </Box>
);

// ── Component --------------------------------------------------------------- //

/**
 * Inline per-stop edit form for run edit mode (phase 2), laid out per the
 * edit-stop comp: external field labels, and a footer bar with the
 * "EDITING STOP N" marker plus Remove / Revert / Done actions.
 *
 * Address changes go through the autocomplete: picking a suggestion resolves
 * it to a location (id + coordinates) so the map marker and downstream
 * geofencing stay correct. Typed-but-unpicked text never stages an address
 * change — the field snaps back to the staged address on blur.
 */
const RunStopEditForm: React.FC<RunStopEditFormProps> = ({
  stopOrder,
  values,
  customerId,
  hasChanges,
  onChange,
  onRevert,
  onRemove,
  onDone,
}) => {
  const theme = useTheme();
  const user = useUser();
  const { suggestions, loading, search, select, clear } = useAddressSuggestions();
  const [addressText, setAddressText] = React.useState(values.address);
  const [resolving, setResolving] = React.useState(false);

  // Phone validation state — same flow as AddAppointmentDialog: regex on
  // blur for the error text, then the shared verifyMobilePhone endpoint for
  // the SMS-capability verdict (advisory adornment; never blocks Done, since
  // a landline is still a valid contact number).
  const [phoneError, setPhoneError] = React.useState<string | null>(null);
  const [isMobilePhone, setIsMobilePhone] = React.useState<boolean | null>(null);
  const [phoneVerifying, setPhoneVerifying] = React.useState(false);

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Stage digits only — comparable with the digits-only baseline, and the
    // format SMS sending expects. The mask is display-only.
    onChange({ customer_phone: phoneDigits(e.target.value) });
    if (phoneError) setPhoneError(null);
    setIsMobilePhone(null); // stale verdict — the number changed
  };

  const handlePhoneBlur = async () => {
    const digits = phoneDigits(values.customer_phone);
    if (!digits) {
      setPhoneError(null);
      setIsMobilePhone(null);
      return;
    }
    if (!validatePhone(digits)) {
      setPhoneError('Enter a valid 10-digit phone number');
      setIsMobilePhone(null);
      return;
    }
    setPhoneError(null);
    setPhoneVerifying(true);
    try {
      const userToken = await user?.getToken();
      const res = await axios({
        method: 'POST',
        url: '/.netlify/functions/verifyMobilePhone',
        data: { phone: digits },
        headers: { authorization: `Bearer ${userToken}` },
      });
      if (res?.data?.success && (res?.data?.isMobile === true || res?.data?.isMobile === false)) {
        setIsMobilePhone(res.data.isMobile);
      } else {
        setIsMobilePhone(null);
      }
    } catch (err) {
      console.error('Failed to verify mobile phone:', err);
      setIsMobilePhone(null);
    } finally {
      setPhoneVerifying(false);
    }
  };

  // Keep the text in sync when the staged address changes from a pick (or a
  // revert restores the baseline).
  React.useEffect(() => {
    setAddressText(values.address);
  }, [values.address]);

  const handlePickSuggestion = async (suggestion: AddressSuggestion) => {
    if (!customerId) {
      toast.error('Missing customer context for address lookup');
      return;
    }
    setResolving(true);
    try {
      const result = await select(suggestion, customerId);
      const loc = result.location;
      if (loc) {
        onChange({
          address: loc.address,
          address_lat: loc.latitude,
          address_lng: loc.longitude,
          location_id: loc.id,
        });
      } else if (result.geocoded) {
        onChange({
          address: result.geocoded.formattedAddress,
          address_lat: result.geocoded.lat,
          address_lng: result.geocoded.lng,
          location_id: null,
        });
      } else {
        toast.error('Could not resolve that address');
      }
    } catch (err) {
      console.error('Address resolution failed:', err);
      toast.error('Could not resolve that address');
    } finally {
      setResolving(false);
    }
  };

  const inputProps = {
    size: 'small' as const,
    fullWidth: true,
    onClick: (e: React.MouseEvent) => e.stopPropagation(),
  };

  return (
    <Box onClick={e => e.stopPropagation()}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Field label='Contact name'>
          <TextField
            {...inputProps}
            value={values.customer_name}
            onChange={e => onChange({ customer_name: e.target.value })}
            slotProps={{ htmlInput: { 'data-testid': 'stop-edit-contact-name-input' } }}
          />
        </Field>

        {/* Address with autocomplete — only a picked suggestion stages a change */}
        <Field label='Address' sx={{ position: 'relative' }}>
          <TextField
            {...inputProps}
            value={addressText}
            helperText={addressText !== values.address ? 'Pick a suggestion to change the address' : undefined}
            onChange={e => {
              setAddressText(e.target.value);
              search(e.target.value);
            }}
            onBlur={() => {
              // Snap back unless the user is clicking a suggestion (which
              // fires before blur resolution via onMouseDown).
              setTimeout(() => {
                setAddressText(values.address);
                clear();
              }, 150);
            }}
            InputProps={{
              endAdornment: loading || resolving ? <CircularProgress size={14} /> : undefined,
            }}
          />
          {suggestions.length > 0 ? (
            <Paper sx={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              zIndex: 10,
              mt: 0.5,
              maxHeight: 200,
              overflowY: 'auto',
              boxShadow: '0px 2px 16px 2px rgba(32,36,48,0.16)',
            }}>
              <List dense disablePadding>
                {suggestions.map((s: AddressSuggestion) => (
                  <ListItemButton
                    key={s.placeId}
                    onMouseDown={e => {
                      e.preventDefault();
                      handlePickSuggestion(s);
                    }}
                  >
                    <ListItemText
                      primary={<Typography variant='body2'>{s.mainText}</Typography>}
                      secondary={<Typography variant='caption' color='text.secondary'>{s.secondaryText}</Typography>}
                    />
                  </ListItemButton>
                ))}
              </List>
            </Paper>
          ) : null}
        </Field>

        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <Field label='Phone' sx={{ flex: 1 }}>
            <TextField
              {...inputProps}
              value={formatPhoneProgressive(values.customer_phone)}
              onChange={handlePhoneChange}
              onBlur={handlePhoneBlur}
              error={Boolean(phoneError)}
              helperText={phoneError}
              slotProps={{
                htmlInput: { maxLength: 14 },
                input: {
                  endAdornment: values.customer_phone.trim() ? (
                    <InputAdornment position='end'>
                      {phoneVerifying ? (
                        <Tooltip title='Validating...'>
                          <span style={{ display: 'inline-flex' }}>
                            <CircularProgress size={16} />
                          </span>
                        </Tooltip>
                      ) : isMobilePhone === true ? (
                        <Tooltip title='Phone is able to receive SMS.'>
                          <span style={{ display: 'inline-flex' }}>
                            <BiSolidCheckCircle size={18} style={{ color: theme.palette.success.main }} />
                          </span>
                        </Tooltip>
                      ) : isMobilePhone === false ? (
                        <Tooltip title='Phone is NOT able to receive SMS!'>
                          <span style={{ display: 'inline-flex' }}>
                            <BiErrorCircle size={18} style={{ color: theme.palette.error.main }} />
                          </span>
                        </Tooltip>
                      ) : (
                        <Tooltip title='Please enter a valid mobile phone number with SMS capability. Do NOT include the country code.'>
                          <span style={{ display: 'inline-flex' }}>
                            <BiMinusCircle size={18} style={{ color: theme.palette.text.disabled }} />
                          </span>
                        </Tooltip>
                      )}
                    </InputAdornment>
                  ) : undefined,
                },
              }}
            />
          </Field>
          <Field label='Vehicle year' sx={{ width: 110, flexShrink: 0 }}>
            <TextField
              {...inputProps}
              value={values.vehicle_year ?? ''}
              onChange={e => {
                const n = parseInt(e.target.value, 10);
                onChange({ vehicle_year: Number.isFinite(n) ? n : null });
              }}
            />
          </Field>
        </Box>

        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <Field label='Vehicle make' sx={{ flex: 1 }}>
            <TextField
              {...inputProps}
              value={values.vehicle_make}
              onChange={e => onChange({ vehicle_make: e.target.value })}
            />
          </Field>
          <Field label='Vehicle model' sx={{ flex: 1 }}>
            <TextField
              {...inputProps}
              value={values.vehicle_model}
              onChange={e => onChange({ vehicle_model: e.target.value })}
            />
          </Field>
        </Box>

        <Field label='Service description'>
          <TextField
            {...inputProps}
            multiline
            minRows={2}
            value={values.service_description}
            onChange={e => onChange({ service_description: e.target.value })}
          />
        </Field>
      </Box>

      {/* ── Footer: editing marker + Remove / Revert / Done ── */}
      <Divider sx={{ mt: 2, mb: 1.5 }} />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant='overline' sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.75,
          fontWeight: 700,
          letterSpacing: 0.6,
          color: 'primary.main',
          lineHeight: 1,
          flex: 1,
          minWidth: 0,
        }}>
          <BiPencil size={14} />
          Editing Stop {stopOrder}
        </Typography>
        <Button
          size='small'
          color='error'
          startIcon={<BiTrash size={14} />}
          onClick={onRemove}
          sx={{ flexShrink: 0 }}
        >
          Remove
        </Button>
        <Button size='small' variant='outlined' onClick={onRevert} disabled={!hasChanges} sx={{ flexShrink: 0 }}>
          Revert
        </Button>
        <Button size='small' variant='contained' color='primary' onClick={onDone} sx={{ flexShrink: 0 }}>
          Done
        </Button>
      </Box>
    </Box>
  );
};

export default RunStopEditForm;
